import { createTheme, type MantineColorsTuple } from "@mantine/core";

// gMVP brand palette — matches the shield-bot logo.
// Logo anchors: navy ground #0b1b26 (dark[8]), teal #1fa89c (teal[5]),
// light teal #5fe3d3 (teal[3]), deep teal #0f5f6b (teal[8]),
// up #39d98a (profit[4]), down #ff6b57 (loss[4]), antenna gold #f6c453 (gold[4]).
const teal: MantineColorsTuple = [
  "#e3fbf8", "#c2f3ed", "#96e8dd", "#5fe3d3", "#39cfc0",
  "#1fa89c", "#178c84", "#126f6d", "#0f5f6b", "#0a4149",
];
const profitGreen: MantineColorsTuple = [
  "#e4fbf0", "#c3f5dc", "#93ecc0", "#5fe2a4", "#39d98a",
  "#1fc876", "#12ab62", "#0d8b50", "#0a6d40", "#064f2e",
];
const lossRed: MantineColorsTuple = [
  "#fff0ec", "#ffd9d2", "#ffb7aa", "#ff9282", "#ff6b57",
  "#f04d38", "#d33a27", "#ad2d1d", "#872215", "#5f170e",
];
// Cool neutral for light mode, tinted toward teal. [0] = light-mode page bg.
const slate: MantineColorsTuple = [
  "#f4f7f6", "#e6edeb", "#cfdad7", "#a9b9b5", "#7f948f",
  "#5c716c", "#44565a", "#2e3f45", "#1b2a31", "#0b1b26",
];
// Single-use accent (antenna dot, "live" dots, highlights).
const gold: MantineColorsTuple = [
  "#fff8e6", "#feefc4", "#fde39a", "#fad66f", "#f6c453",
  "#e9ad2f", "#c88f1b", "#a37213", "#7d570d", "#5a3e08",
];

// Blue-green navy dark palette tuned to the logo ground.
// Mantine maps: [0]=component text, [2]=dimmed, [4]=border, [6]=card bg,
// [7]=body/navbar bg, [8]=main page bg (AppShell.Main override).
const customDark: MantineColorsTuple = [
  "#dfeef0", // [0] input/component label text in dark
  "#b6cad0", // [1] secondary text
  "#8ea8b1", // [2] c="dimmed" in dark (--mantine-color-dimmed)
  "#627f8a", // [3] placeholder / muted
  "#3e5864", // [4] default border (--mantine-color-default-border)
  "#2a414d", // [5] input background / elevated border
  "#1b2f3c", // [6] card / default component bg (--mantine-color-default)
  "#122331", // [7] body bg — navbar + header (--mantine-color-body)
  "#0b1b26", // [8] page main bg — AppShell.Main override (logo ground)
  "#06111a", // [9] deepest
];

export const theme = createTheme({
  primaryColor: "teal",
  primaryShade: { light: 6, dark: 4 },
  autoContrast: true,
  luminanceThreshold: 0.45,
  black: "#0b1b26",
  fontFamily:
    "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji",
  fontFamilyMonospace:
    "ui-monospace, SFMono-Regular, Menlo, Monaco, 'Liberation Mono', monospace",
  defaultRadius: "lg",
  colors: { teal, profit: profitGreen, loss: lossRed, slate, gold, dark: customDark },
  headings: {
    fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji",
    fontWeight: "700",
    sizes: {
      h1: { fontSize: "3rem", lineHeight: "1.15" },
      h2: { fontSize: "2.25rem", lineHeight: "1.2" },
      h3: { fontSize: "1.75rem", lineHeight: "1.25" },
      h4: { fontSize: "1.4rem", lineHeight: "1.3" },
      h5: { fontSize: "1.1rem", lineHeight: "1.35" },
      h6: { fontSize: "1rem", lineHeight: "1.4" },
    },
  },
  components: {
    Table: { defaultProps: { striped: false, withTableBorder: true } },
    Badge: { defaultProps: { variant: "light" } },
    Button: { defaultProps: { radius: "lg" } },
    Card: { defaultProps: { radius: "lg", withBorder: true } },
    AppShell: {
      styles: {
        navbar: { background: "light-dark(#ffffff, var(--mantine-color-dark-7))" },
        header: { background: "light-dark(#ffffff, var(--mantine-color-dark-7))" },
      },
    },
  },
});

/** Semantic helpers for charts / badges so profit & loss never drift from the logo. */
export const brand = {
  up: "var(--mantine-color-profit-4)",
  down: "var(--mantine-color-loss-4)",
  accent: "var(--mantine-color-teal-4)",
  accentStrong: "var(--mantine-color-teal-6)",
  gold: "var(--mantine-color-gold-4)",
  ground: "var(--mantine-color-dark-8)",
} as const;

/** Decimal-string -> compact display.  All numeric UI uses tabular numerals. */
export const monoStyle = { fontFamily: "var(--mantine-font-family-monospace)", fontVariantNumeric: "tabular-nums" } as const;
